import React from 'react';
import { AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';

interface AdminUsersErrorFallbackProps {
  error: string;
  onRetry: () => void;
  loading: boolean;
}

const AdminUsersErrorFallback: React.FC<AdminUsersErrorFallbackProps> = ({
  error,
  onRetry,
  loading
}) => {
  return (
    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
      <div className="flex items-start space-x-3">
        <AlertTriangle className="h-6 w-6 text-yellow-600 mt-1 flex-shrink-0" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-yellow-800 mb-2">
            Admin Users Feature Temporarily Unavailable
          </h3>
          
          <p className="text-yellow-700 mb-4">
            {error}
          </p>
          
          <div className="bg-white rounded-md p-4 border border-yellow-200 mb-4">
            <h4 className="font-medium text-yellow-800 mb-2">Technical Details:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• <strong>Status:</strong> Backend endpoint `/api/admin-users` returning 500 error</li>
              <li>• <strong>Impact:</strong> Cannot fetch, create, or manage admin users</li>
              <li>• <strong>Other features:</strong> Working normally (events, authentication, etc.)</li>
            </ul>
          </div>

          <div className="bg-white rounded-md p-4 border border-yellow-200 mb-4">
            <h4 className="font-medium text-yellow-800 mb-2">For Backend Developers:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Check backend console/logs for database connection errors</li>
              <li>• Verify the admin-users table exists and is accessible</li>
              <li>• Check if the endpoint has proper authentication handling</li>
              <li>• Ensure all required environment variables are set</li>
            </ul>
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={onRetry}
              disabled={loading}
              className="flex items-center px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              {loading ? 'Checking...' : 'Retry Connection'}
            </button>
            
            <a
              href="/admin"
              className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Go to Main Admin
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersErrorFallback;